/**
 * ImportExportRecord 组件
 *
 * 功能说明：
 * 该组件用于展示导入/导出操作的历史记录，包括操作人、操作时间、操作内容、状态和附件下载等信息。
 *
 * 参数说明：
 * - sceneCode: 必填，场景编码，用于区分不同业务场景的导入导出记录
 *   场景编码对应关系：
 *   1 - 车辆备案信息
 *   2 - 点位信息
 *   3 - 工单信息
 *   4 - 违规信息
 *   5 - 预计统计
 *   6 - 手机蓝牙嗅探
 *   7 - 硬件蓝牙嗅探
 *   8 - 核验信息
 *   9 - 秩序区域
 *   10 - 巡检明细
 *   11 - 定位上报
 *   12 - 运维人员
 *   13 - 运维车辆
 * - buttonText: 可选，按钮文本，默认为"导出记录"
 * - title: 可选，弹窗标题，默认为"导出记录"
 * - operateType: 可选，操作类型，1-导入数据，2-导出数据
 */
import React, { useState, useEffect } from 'react'
import { Modal, Form, Table, Button, Tooltip } from 'antd'
import moment from 'moment'
import { useAppSelector } from '@/utils/hooks'
import request from '@/utils/request'
import { IForm } from '@/components'

import type { FormItemConfig } from '@/components/formItem'

interface ImportExportRecordProps {
  sceneCode: number
  buttonText?: string
  title?: string
  operateType?: number
}

const operateTypeOptions = [
  { label: '导入数据', value: 1 },
  { label: '导出数据', value: 2 },
]

const operateStatusOptions = [
  { label: '导入中', value: 1 },
  { label: '导入成功', value: 2 },
  { label: '导入失败', value: 3 },
  { label: '导出中', value: 4 },
  { label: '导出成功', value: 5 },
  { label: '导出失败', value: 6 },
]

const getOperateStatusText = (status: number) => {
  const statusMap: Record<number, { text: string; color: string }> = {
    1: { text: '导入中', color: '#1890ff' },
    2: { text: '导入成功', color: '#52c41a' },
    3: { text: '导入失败', color: '#f5222d' },
    4: { text: '导出中', color: '#1890ff' },
    5: { text: '导出成功', color: '#52c41a' },
    6: { text: '导出失败', color: '#f5222d' },
  }

  return statusMap[status] || { text: '-', color: '#000000' }
}

const getOperateTypeText = (type: number) => {
  return type === 1 ? '导入数据' : '导出数据'
}

const ImportExportRecord: React.FC<ImportExportRecordProps> = ({
  sceneCode,
  buttonText = '导出记录',
  title = '导出记录',
  operateType,
}) => {
  const [visible, setVisible] = useState(false)
  const [form] = Form.useForm()
  const [app] = useAppSelector(state => [state.app])
  const [records, setRecords] = useState([])
  const [total, setTotal] = useState(0)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    entityId: app.currentEntity.entityId,
  })

  const currentUser = app.userInfo?.userName || ''
  const isAdmin = app.userInfo?.roleType === 0

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        operatorName: currentUser,
        timeRange: [moment().startOf('day'), moment()],
        operateType: operateType,
      })
      fetchRecords()
    }
  }, [visible])

  const fetchRecords = async (params = {}) => {
    const values = form.getFieldsValue()
    const timeRange = values.timeRange

    const requestParams = {
      ...pagination,
      sceneCode,
      operatorName: values.operatorName,
      operateType: values.operateType,
      operateStatus: values.operateStatus,
      startTime: timeRange && timeRange[0] ? timeRange[0].format('YYYY-MM-DD HH:mm:ss') : null,
      endTime: timeRange && timeRange[1] ? timeRange[1].format('YYYY-MM-DD HH:mm:ss') : null,
      ...params,
    }

    request('importExportRecord', { data: requestParams }).then(res => {
      if (res?.code === 0) {
        setRecords(res.data.list || [])
        setTotal(res.data.total || 0)
      }
    })
  }

  const handleFinish = () => {
    setPagination({ ...pagination, page: 1 })
    fetchRecords({ page: 1 })
  }

  const handleClear = () => {
    form.resetFields()
  }

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({ ...pagination, page, pageSize })
    fetchRecords({ page, pageSize })
  }

  const handleClick = () => {
    setVisible(true)
  }

  const handleCancel = () => {
    setVisible(false)
  }

  // 处理下载点击事件
  const handleDownload = (url: string) => {
    window.open(url)
  }

  const formItemsConfig: FormItemConfig[] = [
    {
      type: 'input',
      formItemProps: {
        label: '操作人',
        name: 'operatorName',
      },
      childProps: {
        placeholder: '请输入操作人',
        disabled: !isAdmin,
      },
    },
    {
      type: 'dateRangePicker',
      formItemProps: {
        label: '操作时间',
        name: 'timeRange',
      },
      childProps: {
        showTime: true,
        format: 'YYYY-MM-DD HH:mm:ss',
      },
      span: 13,
    },
    {
      type: 'select',
      formItemProps: {
        label: '操作状态',
        name: 'operateStatus',
      },
      childProps: {
        placeholder: '请选择操作状态',
        options: operateStatusOptions,
        allowClear: true,
      },
    },
    {
      type: 'select',
      formItemProps: {
        label: '操作内容',
        name: 'operateType',
      },
      childProps: {
        placeholder: '请选择操作内容',
        options: operateTypeOptions,
        allowClear: true,
      },
    },
  ]

  const columns = [
    {
      title: '操作人',
      dataIndex: 'operatorName',
      key: 'operatorName',
    },
    {
      title: '操作时间',
      dataIndex: 'operateTime',
      key: 'operateTime',
    },
    {
      title: '操作内容',
      dataIndex: 'operateType',
      key: 'operateType',
      render: (type: number) => getOperateTypeText(type),
    },
    {
      title: '状态',
      dataIndex: 'operateStatus',
      key: 'operateStatus',
      render: (status: number, record: any) => {
        const { text, color } = getOperateStatusText(status)
        return (
          <Tooltip title={[3, 6].includes(status) ? record.failReason : ''}>
            <span style={{ color }}>{text}</span>
          </Tooltip>
        )
      },
    },
    {
      title: '附件',
      dataIndex: 'attachmentUrl',
      key: 'attachmentUrl',
      render: (url: string, record: any) => {
        const isSuccess =
          (record.operateStatus === 2 || record.operateStatus === 5) && record.attachmentUrl
        return (
          <Button type="link" disabled={!isSuccess} onClick={() => handleDownload(url)}>
            点击下载数据
          </Button>
        )
      },
    },
  ]

  return (
    <>
      <Button type="primary" onClick={handleClick}>
        {buttonText}
      </Button>
      <Modal title={title} open={visible} onCancel={handleCancel} width={900} footer={null}>
        <div className="mb20">
          <IForm
            form={form}
            formItemsConfig={formItemsConfig}
            onFinish={handleFinish}
            onClear={handleClear}
            divide={8}
          />
        </div>

        <Table
          columns={columns}
          dataSource={records}
          rowKey={record => record.id}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total,
            size: 'default',
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: handleTableChange,
            position: ['bottomRight'],
          }}
        />
      </Modal>
    </>
  )
}

export default ImportExportRecord
